/**
 * Capabilities события по категории: score-based vs legacy режим, дефолтная DrawPolicy,
 * генерация списка допустимых исходов и подсказок по применимым scoring-правилам.
 *
 * Используется в форме события (CreateEventPage), форме результата (EventViewPage)
 * и других местах, где нужно понять, поддерживает ли категория Scoring v2.
 */

import { DrawPolicy, OutcomeCode, outcomeCodeShortLabel } from './mainOutcomeHelpers';

// ===================================================================================================================
// = Mapping категория → DrawPolicy
// ===================================================================================================================

const CATEGORY_DEFAULT_DRAW_POLICY = Object.freeze({
  FOOTBALL: DrawPolicy.DRAW_ALLOWED,
  HOCKEY: DrawPolicy.DRAW_ALLOWED,
  BASKETBALL: DrawPolicy.DRAW_ALLOWED,
  CYBERSPORT: DrawPolicy.DRAW_ALLOWED,
  TENNIS: DrawPolicy.DRAW_FORBIDDEN,
  VOLLEYBALL: DrawPolicy.DRAW_FORBIDDEN,
  // OTHER — нет дефолта, legacy режим
});

/**
 * Возвращает true, если категория поддерживает Scoring v2 structured outcomes.
 */
export function isScoreBasedCategory(category) {
  return Object.prototype.hasOwnProperty.call(CATEGORY_DEFAULT_DRAW_POLICY, category);
}

/**
 * Дефолтная политика ничьей по категории. Для OTHER возвращает null.
 */
export function getDefaultDrawPolicy(category) {
  return CATEGORY_DEFAULT_DRAW_POLICY[category] || null;
}

// ===================================================================================================================
// = Генерация исходов по DrawPolicy
// ===================================================================================================================

/**
 * Возвращает список строк-исходов для score-based события на основе DrawPolicy.
 * Эти строки идентичны именам OutcomeCode и попадают в Event.outcomes.
 */
export function generateOutcomesFromDrawPolicy(drawPolicy) {
  switch (drawPolicy) {
    case DrawPolicy.DRAW_ALLOWED:
      return [OutcomeCode.P1, OutcomeCode.X, OutcomeCode.P2];
    case DrawPolicy.DRAW_FORBIDDEN:
      return [OutcomeCode.P1, OutcomeCode.P2];
    case DrawPolicy.DRAW_WITH_TIE_BREAK_WINNER:
      return [OutcomeCode.P1, OutcomeCode.P2, OutcomeCode.X1, OutcomeCode.X2];
    default:
      return [];
  }
}

// ===================================================================================================================
// = Подсказки UI
// ===================================================================================================================

const DRAW_POLICY_OPTIONS = Object.freeze([
  { value: DrawPolicy.DRAW_ALLOWED, label: 'Ничья допустима (P1 / X / P2)' },
  { value: DrawPolicy.DRAW_FORBIDDEN, label: 'Без ничьей (P1 / P2)' },
  { value: DrawPolicy.DRAW_WITH_TIE_BREAK_WINNER, label: 'Ничья с тай-брейком (P1 / P2 / X1 / X2)' },
]);

/**
 * Полный список опций DrawPolicy для select-а в форме события.
 */
export function getDrawPolicyOptions() {
  return DRAW_POLICY_OPTIONS;
}

/**
 * Возвращает короткое описание правил Scoring v2, применимых для score-based события.
 * Используется как подсказка в форме события.
 */
export function getApplicableScoringRules() {
  return [
    { code: 'EXACT_SCORE', label: 'Точный счёт' },
    { code: 'SCORE_DIFFERENCE', label: 'Точная разница (с верным победителем)' },
    { code: 'CORRECT_WINNER', label: 'Верный победитель' },
  ];
}

/**
 * Возвращает форматированную строку с допустимыми исходами для DrawPolicy.
 * Пример: "P1 / X / P2".
 */
export function formatAllowedOutcomes(drawPolicy) {
  return generateOutcomesFromDrawPolicy(drawPolicy)
    .map(outcomeCodeShortLabel)
    .join(' / ');
}
